/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import DeckGL from '@deck.gl/react';
import bbox from '@turf/bbox';
import { AllGeoJSON } from '@turf/helpers';
import { DeckGLViewPort, SensorPositionData } from './types';
import { createTileLayer, LocationStateProps, MAP_DEFAULT_PITCH, MAX_ZOOM } from './mapHelpers';
import MapToolbar from './MapToolbar';
import { GeoJSON, LocationType, Position } from '../../services/api';
import useStyles from '../../styles';
import {
  getActiveMarker,
  getCurrentLocation,
  getCurrentLocationChildLocations,
  getLocationsById,
  getLocSensorStatusData,
  getMotionThreshold,
  getThemeMode,
  getUserLocation,
  getUserPosition,
} from '../../state/selectors';
import { useAppSelector } from '../../state/store';
import get3DBuildingLayer from './MapLayers/3dBuildingLayer';
import getPosFloorLayers from './MapLayers/posFloorLayers';

interface PositioningMapProps {
  floorPlan: GeoJSON | undefined;
  onChange: (newPosition: SensorPositionData) => void;
  position: SensorPositionData | undefined;
  mapName: string;
  gatewayPosition?: Position;
}

function PositioningMap({
  floorPlan,
  onChange,
  position, // sensor pin position
  mapName,
  gatewayPosition, // position of highstrength gateway that shows sensor wifi pin
}: PositioningMapProps): JSX.Element {
  const classes = useStyles();
  const themeMode = useAppSelector(getThemeMode);
  const userPosition = useAppSelector(getUserPosition);
  const userLocation = useAppSelector(getUserLocation);
  const currentLocation = useAppSelector(getCurrentLocation);
  const locationType = useAppSelector(getLocationsById).get(currentLocation)?.type;
  const childLocations = useAppSelector(getCurrentLocationChildLocations);
  const activeMarker = useAppSelector(getActiveMarker);
  const locSensorStatusData = useAppSelector(getLocSensorStatusData);
  const motionThreshold = useAppSelector(getMotionThreshold);

  const isBuilding = locationType === LocationType.Building;

  const targetPosition = userPosition || position;

  const defaultLocation: LocationStateProps = {
    latitude: targetPosition?.lat ?? 53,
    longitude: targetPosition?.lng ?? -1,
    zoom: targetPosition?.lat && targetPosition?.lng ? 21 : 4,
    pitch: MAP_DEFAULT_PITCH,
    bearing: 0,
    transitionDuration: 500,
    maxZoom: MAX_ZOOM,
  };

  const [hoveredPolygon, setHoveredPolygon] = useState('');
  const [mapLocationState, setMapLocationState] = useState(defaultLocation);

  const tileLayer = useRef(
    createTileLayer(themeMode, mapName, (obj: any) => {
      if (obj?.picked) {
        const coords = obj.coordinate;
        if (coords) {
          onChange({
            ...position,
            lng: Math.round(coords[0] * 1e7) / 1e7,
            lat: Math.round(coords[1] * 1e7) / 1e7,
          });
        }
      }
    })
  );

  const fitToView = () => {
    if (!tileLayer.current?.context || !floorPlan?.features?.length) {
      return;
    }

    const currentLocationCoordinates = {
      minLat: 90,
      minLon: 180,
      maxLat: -90,
      maxLon: -180,
    };

    const [minLng, minLat, maxLng, maxLat] = bbox(floorPlan as AllGeoJSON); // Turf.js

    if (minLng < currentLocationCoordinates.minLon) {
      currentLocationCoordinates.minLon = minLng;
    }
    if (maxLng > currentLocationCoordinates.maxLon) {
      currentLocationCoordinates.maxLon = maxLng;
    }
    if (minLat < currentLocationCoordinates.minLat) {
      currentLocationCoordinates.minLat = minLat;
    }
    if (maxLat > currentLocationCoordinates.maxLat) {
      currentLocationCoordinates.maxLat = maxLat;
    }

    const { viewport } = tileLayer.current.context;
    // @ts-ignore
    const { longitude, latitude, zoom } = viewport.fitBounds([
      [currentLocationCoordinates.minLon, currentLocationCoordinates.minLat],
      [currentLocationCoordinates.maxLon, currentLocationCoordinates.maxLat],
    ]);

    const newViewState = {
      ...mapLocationState,
      latitude: targetPosition?.lat ?? latitude,
      longitude: targetPosition?.lng ?? longitude,
      zoom: zoom <= 19 && !isBuilding ? 19 : zoom,
    };
    setMapLocationState({ ...newViewState });
  };

  useEffect(() => {
    fitToView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPlan, tileLayer.current.context]);

  // @ts-ignore
  const viewStateUpdateHandler = ({ viewState }) => {
    const { longitude, latitude, zoom, pitch, bearing } = viewState as DeckGLViewPort;
    const newViewState = {
      ...mapLocationState,
      longitude,
      latitude,
      zoom,
      pitch,
      bearing,
    };

    setMapLocationState(newViewState);
  };

  const deckGLLayers = useMemo(() => {
    let layer = null;
    const tileRef = [tileLayer.current];

    if (isBuilding && floorPlan) {
      const buildingLayer = get3DBuildingLayer(
        currentLocation,
        childLocations,
        activeMarker,
        locSensorStatusData,
        floorPlan,
        motionThreshold
      );
      layer = [...tileRef, ...[buildingLayer]];
    } else {
      const floorlayer = getPosFloorLayers(
        floorPlan,
        hoveredPolygon,
        setHoveredPolygon,
        onChange,
        position,
        mapLocationState,
        gatewayPosition,
        userPosition,
        userLocation,
        currentLocation
      );
      layer = [...tileRef, ...floorlayer];
    }

    return layer;
  }, [
    activeMarker,
    childLocations,
    currentLocation,
    floorPlan,
    gatewayPosition,
    hoveredPolygon,
    isBuilding,
    locSensorStatusData,
    mapLocationState,
    motionThreshold,
    onChange,
    position,
    userLocation,
    userPosition,
  ]);

  const resetOrientation = () => {
    const newViewState = { ...mapLocationState, pitch: MAP_DEFAULT_PITCH, bearing: 0 };
    setMapLocationState({ ...newViewState });
  };

  // Auto-zoom on initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const afterRenderHandler = () => {
    // Auto-zoom on intial map load
    if (isInitialLoad) {
      setIsInitialLoad(false);
      fitToView();
    }
  };
  // End of Auto-zoom handling

  return (
    <div className={classes.mapContainer}>
      <div style={{ height: '100%', width: '100%', position: 'absolute' }}>
        <DeckGL
          initialViewState={mapLocationState}
          controller={{ touchRotate: true }}
          id="deckgl-map"
          // @ts-ignore
          layers={deckGLLayers}
          parameters={{ depthMask: false }}
          onViewStateChange={viewStateUpdateHandler}
          onAfterRender={afterRenderHandler}
        />
        <MapToolbar
          fitToView={fitToView}
          resetOrientation={resetOrientation}
          pitch={mapLocationState.pitch}
          bearing={mapLocationState.bearing}
        />
      </div>
    </div>
  );
}

PositioningMap.defaultProps = {
  gatewayPosition: undefined,
};

export default PositioningMap;
